import React from 'react';
import Icon from '../../../components/AppIcon';

const MetricsCards = ({ metricsData }) => {
  const {
    totalProcessingTime,
    averageProcessingTime,
    zatcaComplianceRate,
    errorCategories,
    peakProcessingHour,
    systemPerformance
  } = metricsData;

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getPerformanceColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 70) return 'text-warning';
    return 'text-error';
  };

  const getPerformanceIcon = (score) => {
    if (score >= 90) return 'TrendingUp';
    if (score >= 70) return 'Minus';
    return 'TrendingDown';
  };

  return (
    <div className="space-y-6">
      <div className="bg-muted rounded-lg border border-border p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-foreground mb-4">Processing Metrics</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Clock" size={18} color="var(--color-text-secondary)" />
              <span className="text-sm font-medium text-muted-foreground">Total Processing Time</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{formatTime(totalProcessingTime)}</p>
            <p className="text-xs text-muted-foreground mt-1">Complete batch duration</p>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Zap" size={18} color="var(--color-text-secondary)" />
              <span className="text-sm font-medium text-muted-foreground">Average Per Invoice</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{formatTime(averageProcessingTime)}</p>
            <p className="text-xs text-muted-foreground mt-1">Mean processing time</p>
          </div>

          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Shield" size={18} color="var(--color-accent)" />
              <span className="text-sm font-medium text-accent">ZATCA Compliance</span>
            </div>
            <p className="text-2xl font-bold text-accent">{zatcaComplianceRate}%</p>
            <p className="text-xs text-accent/80 mt-1">Regulatory clearance rate</p>
          </div>
        </div>
      </div>
      <div className="bg-muted rounded-lg border border-border p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-foreground mb-4">Error Analysis</h3>
        
        <div className="space-y-3">
          {errorCategories?.map((category, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${
                  category?.severity === 'high' ? 'bg-error' :
                  category?.severity === 'medium' ? 'bg-warning' : 'bg-success'
                }`} />
                <div>
                  <p className="font-medium text-foreground">{category?.type}</p>
                  <p className="text-sm text-muted-foreground">{category?.description}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-foreground">{category?.count}</p>
                <p className="text-xs text-muted-foreground">occurrences</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-muted rounded-lg border border-border p-6 shadow-sm">
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="BarChart3" size={18} color="var(--color-text-secondary)" />
            <h3 className="text-lg font-semibold text-foreground">Peak Processing</h3>
          </div>
          
          <div className="text-center">
            <p className="text-3xl font-bold text-foreground mb-2">{peakProcessingHour}</p>
            <p className="text-sm text-muted-foreground">Highest activity period</p>
            <div className="mt-3 p-2 bg-muted rounded">
              <p className="text-xs text-muted-foreground">
                Most invoices processed during this hour
              </p>
            </div>
          </div>
        </div>

        <div className="bg-muted rounded-lg border border-border p-6 shadow-sm">
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="Activity" size={18} color="var(--color-text-secondary)" />
            <h3 className="text-lg font-semibold text-foreground">System Performance</h3>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Icon 
                name={getPerformanceIcon(systemPerformance?.score)} 
                size={20} 
                color={`var(--color-${systemPerformance?.score >= 90 ? 'success' : systemPerformance?.score >= 70 ? 'warning' : 'error'})`}
              />
              <p className={`text-3xl font-bold ${getPerformanceColor(systemPerformance?.score)}`}>
                {systemPerformance?.score}%
              </p>
            </div>
            <p className="text-sm text-muted-foreground mb-3">{systemPerformance?.status}</p>
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">CPU Usage:</span>
                <span className="text-foreground">{systemPerformance?.cpuUsage}%</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Memory:</span>
                <span className="text-foreground">{systemPerformance?.memoryUsage}%</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Throughput:</span>
                <span className="text-foreground">{systemPerformance?.throughput} inv/min</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricsCards;

